import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigation } from '../../public/context/NavigationContext';
import { AdminDashboard } from './AdminDashboard';
import { AdminThemeProvider } from '../../theme/AdminThemeProvider';
import {
  ShieldAlert,
  ShieldCheck,
  ArrowRight,
  ArrowLeft,
  LayoutDashboard,
  LogOut,
  Sparkles,
  ExternalLink,
} from 'lucide-react';

export const AdminLayout: React.FC = () => {
  const { currentUser, isLoading, isAuthenticated, isAdmin, isSuperAdmin, logout } = useAuth();
  const { navigate } = useNavigation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-100 p-4">
        <div className="w-12 h-12 rounded-2xl bg-rose-600/20 border border-rose-500/30 flex items-center justify-center mb-4">
          <ShieldCheck className="w-6 h-6 text-rose-400 animate-pulse" />
        </div>
        <p className="text-sm font-semibold text-slate-300">Yönetici yetkileri doğrulanıyor...</p>
      </div>
    );
  }

  // 1. Unauthenticated check
  if (!isAuthenticated || !currentUser) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-100 p-4 text-center">
        <div className="w-16 h-16 rounded-3xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center mb-4">
          <ShieldAlert className="w-8 h-8 text-rose-400" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Yönetici Girişi Gerekli</h2>
        <p className="text-sm text-slate-400 max-w-md mb-6 leading-relaxed">
          Adiosoft Yönetim platform kontrol paneline erişmek için lütfen yetkili yönetici hesabınızla giriş yapınız.
        </p>
        <button
          onClick={() => navigate('/giris')}
          className="px-6 py-3 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-sm font-bold shadow-lg shadow-rose-600/30 transition flex items-center gap-2"
        >
          <span>Giriş Yap</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // 2. Authorization check (Authoritative: Only admin@adiosoft.com with isSuperAdmin verified)
  const hasAdminAccess = currentUser?.email?.toLowerCase() === 'admin@adiosoft.com' && isSuperAdmin;
  if (!hasAdminAccess) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-100 p-4 text-center">
        <div className="w-16 h-16 rounded-3xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-4">
          <ShieldAlert className="w-8 h-8 text-amber-400" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Erişim Reddedildi (403 - Yetkisiz Erişim)</h2>
        <p className="text-sm text-slate-400 max-w-md mb-6 leading-relaxed">
          Bu alan yalnızca yetkili Adiosoft Platform Yöneticilerine açıktır. Müşteri hesabınızla bu panele erişim yetkiniz bulunmamaktadır.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition flex items-center gap-2 shadow-lg shadow-blue-600/25"
          >
            <LayoutDashboard className="w-4 h-4" />
            <span>Müşteri Paneline Dön</span>
          </button>
          <button
            onClick={() => navigate('/')}
            className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-medium transition flex items-center gap-2 border border-slate-700"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Ana Sayfaya Git</span>
          </button>
        </div>
      </div>
    );
  }

  // 3. Authorized Admin Layout
  return (
    <AdminThemeProvider className="min-h-screen flex flex-col font-sans antialiased">
      {/* Dedicated Admin Top Header */}
      <header className="h-16 border-b border-rose-900/30 bg-slate-900/90 backdrop-blur-md px-4 sm:px-6 flex items-center justify-between z-30 sticky top-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-rose-600 to-red-700 flex items-center justify-center text-white font-black shadow-lg shadow-rose-600/30">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-extrabold text-sm text-white tracking-tight">ADIOSOFT</span>
              <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-rose-500/20 text-rose-300 border border-rose-500/30">
                Sistem Yöneticisi
              </span>
            </div>
            <div className="text-[11px] text-slate-400">Merkezi Platform Yönetimi & Altyapı Denetimi</div>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => navigate('/')}
            title="Adiosoft Ana Sayfası"
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 text-xs font-medium transition"
          >
            <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
            <span>Ana Sayfa</span>
          </button>

          <div className="h-4 w-px bg-slate-800 mx-1 hidden sm:block" />

          {/* Admin User Info */}
          <div className="flex items-center gap-2 pl-1">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-600 to-amber-600 flex items-center justify-center text-white text-xs font-bold">
              {currentUser.displayName?.charAt(0) || currentUser.email?.charAt(0) || 'A'}
            </div>
            <div className="hidden lg:block text-left text-xs">
              <div className="font-semibold text-white truncate max-w-[140px] leading-tight">
                {currentUser.displayName || currentUser.email}
              </div>
              <div className="text-[10px] text-rose-400 font-mono">Platform Admin</div>
            </div>
          </div>

          <button
            onClick={async () => {
              await logout();
              navigate('/giris');
            }}
            className="p-2 rounded-xl bg-rose-950/40 hover:bg-rose-900/60 text-rose-300 border border-rose-800/40 transition"
            title="Çıkış Yap"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Main Admin Dashboard Content */}
      <main className="flex-1 overflow-y-auto">
        <AdminDashboard />
      </main>
    </AdminThemeProvider>
  );
};
